/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addressApi } from '../../services/subscriptionApi';
import { formatApiError } from '../../utils';

// Types
export interface Address {
  id: number;
  label: string;
  full_name: string;
  phone: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string;
  pincode: string;
  landmark: string | null;
  is_default: boolean;
  is_serviceable: boolean;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceabilityResult {
  serviceable: boolean;
  message: string;
  delivery_slots?: string[];
  estimated_delivery?: string;
  zone_id?: number | null;
  zone_name?: string | null;
  city?: string | null;
  base_delivery_fee?: number | null;
}

interface AddressState {
  addresses: Address[];
  selectedAddress: Address | null;
  loading: boolean;
  error: string | null;
  serviceability: ServiceabilityResult | null;
}

const initialState: AddressState = {
  addresses: [],
  selectedAddress: null,
  loading: false,
  error: null,
  serviceability: null,
};

// Async thunks
export const fetchAddresses = createAsyncThunk(
  'address/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await addressApi.list();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch addresses');
    }
  }
);

export const createAddress = createAsyncThunk(
  'address/create',
  async (data: Omit<Address, 'id' | 'created_at' | 'updated_at'>, { rejectWithValue }) => {
    try {
      const response = await addressApi.create(data);
      return response.data;
    } catch (error: any) {
      // 422 detail is an array of objects; flatten so it can be shown as text
      return rejectWithValue(formatApiError(error.response?.data?.detail, 'Failed to create address'));
    }
  }
);

export const updateAddress = createAsyncThunk(
  'address/update',
  async ({ id, data }: { id: number; data: Partial<Address> }, { rejectWithValue }) => {
    try {
      const response = await addressApi.update(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(formatApiError(error.response?.data?.detail, 'Failed to update address'));
    }
  }
);

export const deleteAddress = createAsyncThunk(
  'address/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await addressApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete address');
    }
  }
);

export const checkServiceability = createAsyncThunk(
  'address/checkServiceability',
  async (
    arg: string | { pincode: string; latitude?: number | null; longitude?: number | null },
    { rejectWithValue }
  ) => {
    const pincode = typeof arg === 'string' ? arg : arg.pincode;
    const latitude = typeof arg === 'string' ? undefined : arg.latitude;
    const longitude = typeof arg === 'string' ? undefined : arg.longitude;
    try {
      const response = await addressApi.checkServiceability(pincode, latitude, longitude);
      const d = response.data;
      // Backend returns `is_serviceable`; normalize to `serviceable` for the UI.
      const result: ServiceabilityResult = {
        serviceable: !!d.is_serviceable,
        message: d.message,
        zone_id: d.zone_id ?? null,
        zone_name: d.zone_name ?? null,
        city: d.city ?? null,
        base_delivery_fee: d.base_delivery_fee ?? null,
      };
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to check serviceability');
    }
  }
);

// Slice
const addressSlice = createSlice({
  name: 'address',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedAddress: (state, action) => {
      state.selectedAddress = action.payload;
    },
    clearServiceability: (state) => {
      state.serviceability = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch addresses
    builder.addCase(fetchAddresses.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAddresses.fulfilled, (state, action) => {
      state.loading = false;
      state.addresses = action.payload;
      // Auto-select default address if none selected
      if (!state.selectedAddress) {
        const defaultAddr = action.payload.find((a: Address) => a.is_default);
        if (defaultAddr) {
          state.selectedAddress = defaultAddr;
        }
      }
    });
    builder.addCase(fetchAddresses.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create address
    builder.addCase(createAddress.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createAddress.fulfilled, (state, action) => {
      state.loading = false;
      state.addresses.push(action.payload);
      // If this is the first address or it's set as default, select it
      if (action.payload.is_default || state.addresses.length === 1) {
        state.selectedAddress = action.payload;
      }
    });
    builder.addCase(createAddress.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update address
    builder.addCase(updateAddress.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateAddress.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.addresses.findIndex((a) => a.id === action.payload.id);
      if (index !== -1) {
        state.addresses[index] = action.payload;
      }
      if (state.selectedAddress?.id === action.payload.id) {
        state.selectedAddress = action.payload;
      }
    });
    builder.addCase(updateAddress.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete address
    builder.addCase(deleteAddress.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteAddress.fulfilled, (state, action) => {
      state.loading = false;
      state.addresses = state.addresses.filter((a) => a.id !== action.payload);
      if (state.selectedAddress?.id === action.payload) {
        state.selectedAddress = state.addresses.find((a) => a.is_default) || state.addresses[0] || null;
      }
    });
    builder.addCase(deleteAddress.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Check serviceability
    builder.addCase(checkServiceability.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.serviceability = null;
    });
    builder.addCase(checkServiceability.fulfilled, (state, action) => {
      state.loading = false;
      state.serviceability = action.payload;
    });
    builder.addCase(checkServiceability.rejected, (state, action) => {
      state.loading = false;
      state.serviceability = {
        serviceable: false,
        message: action.payload as string,
      };
    });
  },
});

export const { clearError, setSelectedAddress, clearServiceability } = addressSlice.actions;
export default addressSlice.reducer;
